import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { Outlet, useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import ReduxStore from '../../store';
import AppSidebar from '../../sidebar/components/AppSidebar';
import AppTopBar from '../../topbar/components/AppTopBar';
import GrowlContainer from '../../shared/growl/components/GrowlContainer';
import ImportDialog from '../../shared/import/components/ImportDialog';
import useImportDialog from '../../shared/import/hooks/useImportDialog';
import { getConfig } from '../../shared/api/configApi';
import { exportContent, getAssetMetadata } from '../../shared/api/contentApi';
import { addReport, getReports } from '../../shared/api/reportsApi';
import { loadTenantState, setSubtenant, setTenant } from '../../shared/api/tenantApi';
import {
  launchPreview,
  reloadPreview,
  closePreviewWindow,
} from '../../shared/preview/previewWindow';
import ExportContentDialog from './ExportContentDialog';
import { forgetSavedViews } from '../../shared/resourceTable/hooks/usePageTable';
import { useRailCollapsed } from '../../sidebar/railCollapse';
import { registerMenuActions, runMenuAction } from '../menuActions';
import { pathForState, stateForPath } from '../definitions';
import { linkTenantStylesheets } from '../tenantStylesheets';
import log from '../../logger';

/**
 * The CMS frame: the rail, the top bar, the growl container, and the page beside them.
 *
 * Replaces `modules/root/root_controller.js` and `modules/root/root.html`. Those existed to
 * fetch four things and hand them to React across the `react2angular` boundary. With the
 * boundary gone, the fetching moved here and the props stopped making a round trip through
 * `$rootScope`.
 *
 * `css/simple-sidebar.css` and `css/main.css` reach into this markup: `#wrapper` is a flex
 * row of exactly two, the rail and `.app-main`, and the CMS context element sits **outside**
 * `#wrapper` on purpose. It is a real flex item, so putting it inside claims a column of the
 * frame and squeezes the page into a strip. That was the first frame's bug and it went
 * unnoticed for years. `.app-main` is a column: the top bar (breadcrumbs and the account
 * menu), then `#page-content-wrapper`, the page column, which is what scrolls.
 */
const AppFrame = () => {
  const navigate = useNavigate();
  const router = useRouter();
  const [config, setConfig] = useState(null);
  const [configFailed, setConfigFailed] = useState(false);
  const [tenantState, setTenantState] = useState(null);
  const [logoUrl, setLogoUrl] = useState(undefined);
  const { showImportDialog, importDialogProps } = useImportDialog();
  // Here rather than in the rail, because the button that toggles it is in the top bar.
  const [railCollapsed, toggleRail] = useRailCollapsed();
  // null = not exporting; { url } = exported and offering the download; { error } = failed.
  const [exportState, setExportState] = useState(null);
  // The page column, which is what scrolls: see `navigateToMenuItem`.
  const pageColumn = useRef(null);

  useEffect(() => {
    let live = true;
    getConfig()
      .then((value) => {
        // Not before sign-in, or every browser opens its own sign-in dialog. See the file.
        if (value?.loggedInUser) linkTenantStylesheets();
        if (live) setConfig(value);
      })
      .catch((error) => {
        log.error('Failed to load the CMS config', error);
        if (live) setConfigFailed(true);
      });
    loadTenantState()
      .then((value) => live && setTenantState(value))
      .catch((error) => log.error('Failed to load tenant state', error));
    // The tenant's own logo, if it has uploaded one. `instanceMappings` being empty means the
    // asset exists but is not mapped into any instance, which is not the same as having one.
    getAssetMetadata('cms-logo.png')
      .then((meta) => {
        if (!live) return;
        const path = meta?.latestRevision?.previewPath;
        if (path && Object.keys(meta?.instanceMappings || {}).length > 0) setLogoUrl(path);
      })
      .catch(() => {
        // A tenant with no logo answers with an error, which is the normal case. The Angular
        // version swallowed it the same way.
      });
    return () => {
      live = false;
    };
  }, []);

  // The preview window is a popup that outlives this component, so it is closed with the tab
  // rather than on unmount. `window.onbeforeunload` is what root_controller used.
  useEffect(() => {
    const close = () => closePreviewWindow();
    window.addEventListener('beforeunload', close);
    return () => window.removeEventListener('beforeunload', close);
  }, []);

  /*
   * Whether the page has scrolled under the top bar, which then lifts with a shadow. The
   * page column is what scrolls, and it exists only once the frame renders, so this waits
   * for that. React skips the render when the answer has not changed, so a scroll costs one
   * render at the top of the page and none in between.
   */
  const [pageScrolled, setPageScrolled] = useState(false);
  const frameReady = Boolean(config) || configFailed;
  useEffect(() => {
    const column = pageColumn.current;
    if (!column) return undefined;
    const check = () => setPageScrolled(column.scrollTop > 0);
    check();
    column.addEventListener('scroll', check, { passive: true });
    return () => column.removeEventListener('scroll', check);
  }, [frameReady]);

  /**
   * What the rail highlights.
   *
   * `sidebar/definitions.js` matches four ways and needs both the path and the state. Its
   * last branch, an exact state-and-params comparison, is **the only one** that matches
   * Manage Admins, Manage Subtenants and any plugin whose name contains a slash, so the
   * state cannot be left blank: doing that unhighlights all three and the rail looks
   * broken on exactly the screens an admin visits most.
   */
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const route = useMemo(() => ({ path: pathname, ...stateForPath(pathname) }), [pathname]);

  /**
   * Refresh the app preview after a content change.
   *
   * `root_controller` debounced this by a second, and **the debounce is the load-bearing
   * part**, not the broadcast it wrapped. Callers fire per operation: `useItemPersistence`
   * on every save, remove, restore and reorder, and `PdfManagerPage` in four places. Each
   * call runs `reloadPreview`, which polls `checkSnapshotIsComputed` once a second until the
   * snapshot lands and then reloads the window. Undebounced, a reorder or a multi-delete
   * starts one poll and one reload per item.
   */
  const notifyContentUpdated = useMemo(
    () =>
      debounce(() => {
        reloadPreview().catch((error) => log.error('Failed to reload the preview', error));
      }, 1000),
    []
  );
  useEffect(() => () => notifyContentUpdated.cancel(), [notifyContentUpdated]);

  /**
   * `navigationService.exportContent`.
   *
   * The confirmation step is kept. The Angular version built the package, then opened a
   * "Content exported" dialog whose only control was Download. Skipping straight to the
   * download is arguably better, but it is a behaviour change and this is a port.
   */
  const doExportContent = useCallback(async () => {
    try {
      const url = await exportContent();
      setExportState({ url });
    } catch (error) {
      log.error('Content export failed', error);
      setExportState({ error: 'Content export failed.  Please try again later.' });
    }
  }, []);

  /**
   * A side-menu click. `navigationService.navigateToMenuItem`, with its three shapes intact:
   * a state to route to, a named click handler, or an external href.
   */
  const navigateToMenuItem = useCallback(
    (menuItem) => {
      const navigation = menuItem?.navigation;
      if (!navigation) return;
      if (navigation.state) {
        const to = pathForState(navigation.state, navigation.stateParams);
        if (to) {
          /* SURF-4843: a list opened from the rail starts on page one, as the
             `CmsItemListSection` lists do; a list reached back from one of its own records
             is where the admin left it. The External Url, PDF and WYSIWYG lists keep their
             page in sessionStorage, so the rail drops it before the screen mounts. Not when
             the click is for the screen already open: that navigation is a no-op, the list
             does not remount and stays on its page, and the view it saved has to stay with
             it, or opening a row's editor and coming back would land on page one. */
          if (!isEqual(stateForPath(to), stateForPath(pathname))) forgetSavedViews();
          navigate({ to });
          // A menu click opens the screen at its top. This was `window.scrollTo(0, 0)`, which
          // does nothing here: the frame is 100vh with `overflow: hidden`, and the page scrolls
          // inside `#page-content-wrapper`. So the next screen opened as far down as the last
          // one had been scrolled (Groups scrolled down, then ITRS opened at its bottom).
          if (pageColumn.current) pageColumn.current.scrollTop = 0;
        }
        return;
      }
      if (navigation.clickHandler) {
        runMenuAction(navigation.clickHandler);
        return;
      }
      if (navigation.href) window.location = navigation.href;
    },
    [navigate, pathname]
  );

  /**
   * The pointer is over a side-menu item, or the keyboard is on it: start loading the page it
   * opens, so the click usually finds the page's code already there. `preloadRoute` runs the
   * matched route's `loader`, which is what starts the download (`../routes`). Only items that
   * route somewhere have anything to load. A failure here is dropped; the click, if it comes,
   * meets it and handles it.
   */
  const preloadMenuItem = useCallback(
    (menuItem) => {
      const navigation = menuItem?.navigation;
      if (!navigation?.state) return;
      const to = pathForState(navigation.state, navigation.stateParams);
      if (to) router.preloadRoute({ to }).catch(() => {});
    },
    [router]
  );

  // Published so `HomeRedirect` can run them too: a tenant whose home item is Export Content
  // or Preview App has no route to navigate to. See `../menuActions`.
  useEffect(() => {
    registerMenuActions({ exportContent: doExportContent, previewApp: () => launchPreview() });
    return () => registerMenuActions({});
  }, [doExportContent]);

  const cmsContextProps = useMemo(
    () => ({
      cmsConfig: config,
      selectedTenant: tenantState?.selectedTenant,
      selectedSubtenant: tenantState?.selectedSubtenant,
      addReport,
      getReports,
      notifyContentUpdated,
      showImportDialog,
    }),
    [config, tenantState, notifyContentUpdated, showImportDialog]
  );

  /*
   * Nothing renders until the config is in, because every screen reads it out of the CMS
   * context and painting the rail against an absent menu flashes an empty frame.
   *
   * A *failed* config is different, and the Angular frame handled it better than a naive
   * port does: it gated only the inner `.container-fluid` on `ready`, so the rail and the
   * growl container still painted. Returning null on failure gives the admin a white page
   * with nothing to click and no explanation, so a failure renders the frame and says so.
   */
  if (!config && !configFailed) return null;

  return (
    <ReduxStore {...cmsContextProps}>
      <div id="wrapper">
        <AppSidebar
          route={route}
          logoUrl={logoUrl}
          collapsed={railCollapsed}
          onNavigate={navigateToMenuItem}
          onPreload={preloadMenuItem}
        />
        <div className="app-main">
          {/* Outside the page column, so it stays put while the page scrolls under it. It
              holds the rail's collapse button, and the account menu with the tenant and
              subtenant pickers that used to sit under the logo. */}
          <AppTopBar
            route={route}
            onNavigate={navigateToMenuItem}
            onPreload={preloadMenuItem}
            scrolled={pageScrolled}
            railCollapsed={railCollapsed}
            onToggleRail={toggleRail}
            onSelectTenant={setTenant}
            onSelectSubtenant={setSubtenant}
            showTenants={tenantState?.showTenants}
            showSubtenants={tenantState?.showSubtenants}
          />
          <div id="page-content-wrapper" ref={pageColumn}>
            <div className="container-fluid">
              <GrowlContainer groupId="globalroot" />
              {configFailed ? (
                <p className="tw-p-4">
                  The CMS configuration could not be loaded. Reload the page, and if it keeps
                  happening contact your system administrator.
                </p>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </div>
      </div>
      {importDialogProps && <ImportDialog key={importDialogProps.openId} {...importDialogProps} />}
      {exportState && <ExportContentDialog {...exportState} onClose={() => setExportState(null)} />}
    </ReduxStore>
  );
};

export default AppFrame;
